import axios from 'axios'
import { get, merge, omit } from 'lodash'
import { DEFAULT_AXIOS_OPTIONS } from './constants'

const logOutCodes = [
  'loginRequired',
  'sessionNotFound',
  'No active session',
  'The session should be active',
  'The session should be active!'
]

const httpErrorsNotToLog = [
  401,
  403,
  404,
]

const DEFAULT_HOOKS = {
  onRequestError: () => {},
  onLoginRequired: () => {},
}

const DEFAULT_HANDLERS = {
  cache: {
    setItem: () => {},
    getItem: () => {},
  },
}

const DEFAULT_GET_OPTS = {
  useCache: false,
}


export default class Requester {
  set token(value) {
    this._token = value
  }
  constructor({ apiUrl, token, generateCacheKey, cacheIdentifier, requestHeaders, identifier }) {
    this.apiUrl = apiUrl
    this._token = token
    this._identifier = identifier
    this.cacheIdentifier = cacheIdentifier
    this.generateCacheKey = generateCacheKey
    this._requestHeaders = requestHeaders
    this._hooks = DEFAULT_HOOKS
    this._handlers = DEFAULT_HANDLERS
  }

  set hooks(value) {
    this._hooks = merge(DEFAULT_HOOKS, value)
  }
  set handlers(value) {
    this._handlers = merge(DEFAULT_HANDLERS, value)
  }
  set safeMode(value) {
    const safeMode = typeof value === 'string' ? (value === '1' || value === 'true') : value
    this._requestHeaders = merge(this._requestHeaders, { 'x-safemode': safeMode })
  }

  get(endpoint, params, opts = {}) {
    return this._buildRequest('get', endpoint, params, { ...DEFAULT_GET_OPTS, ...opts })
  }

  post(endpoint, data, opts) {
    return this._buildRequest('post', endpoint, data, opts)
  }

  /**
   * Build and execute remote request
   */
  async _buildRequest(method, endpoint, data = {}, opts = {}) {
    if(opts.useAuth !== false && !this._token) {
      throw new Error('Auth token is not defined')
    }

    const url  = `${this.apiUrl}${endpoint}`

    const headers = {
      'content-type': 'application/json',
      ...omit(this._requestHeaders, opts.excludeHeaders || []),
      ...(opts.identifier ? { 'x-identifier': opts.identifier } : {}),
      'x-auth-token': this._token,
    }

    let cacheKey = ''

    //check if request is in sessionStorage
    if(opts.useCache) {
      cacheKey = this.generateCacheKey(endpoint, data, this.cacheIdentifier)
      const response = this._handlers.cache.getItem(cacheKey)
      if(response) {
        return [{ response, cache: true }, null]
      }
    }

    const request = {
      url,
      method,
      headers,
      ...(method === 'get' ? { params: data } : { data }),
      timeout: opts.timeout || DEFAULT_AXIOS_OPTIONS.timeout,
    }

    try {
      const response = await axios(request)
      const result = response.data.result

      if(opts.useCache) {
        this._handlers.cache.setItem(cacheKey, result, opts.expiry || 60 * 60 * 1000)
      }

      return [response.data.result || {}, null]

    } catch (e) {
      if (e.response) {
        // eslint-disable-next-line no-shadow
        const { status, data } = e.response
        if (status === 403 || (status === 400 && data.error && data.error.code && logOutCodes.includes(data.error.code))) {
          if (this._handlers.session && typeof this._handlers.session.logoutSession === 'function') {
            this._handlers.session.logoutSession()
          }
        }
        if (status >= 403) {
          if (data.error && data.error.code && logOutCodes.includes(data.error.code)) {
            this._hooks.onLoginRequired()
          }
        }

        if(!httpErrorsNotToLog.includes(status)) {
          logServiceError(e, this._hooks.onRequestError)
        }
        if(data.error) {
          return [null, data.error]
        }else if(data.message) {
          return [null, { status: status, message: data.message }]
        }else {
          return [null, e]
        }
      } else {
        return [null, e]
      }
      
    }
  }
}


function logServiceError(error, onRequestError) {
  const { config: { url }, response: { data, status, headers } } = error

  onRequestError({
    correlationId: get(headers, 'x-amzn-requestid'),
    endpoint: url,
    statusCode: status,
    response: data,
  })
}
